<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { useCartStore } from '@/stores/cart'
import { useFormatters } from '@/composables/useFormatters'
import {
  XMarkIcon,
  TrashIcon,
  PlusIcon,
  MinusIcon,
  ShoppingBagIcon,
} from '@heroicons/vue/24/outline'
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/vue'

const cartStore = useCartStore()
const { formatCurrency } = useFormatters()

const isOpen = computed({
  get: () => cartStore.isOpen,
  set: (value: boolean) => {
    if (!value) cartStore.closeDrawer()
  }
})
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="relative z-50" @close="cartStore.closeDrawer">
      <!-- Backdrop -->
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-300"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
            >
              <DialogPanel class="pointer-events-auto w-screen max-w-md">
                <div class="flex h-full flex-col bg-white shadow-xl">
                  <!-- Header -->
                  <div class="flex items-center justify-between px-4 py-4 border-b border-gray-200">
                    <DialogTitle class="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <ShoppingBagIcon class="w-6 h-6" />
                      Carrinho
                      <span v-if="cartStore.itemCount > 0" class="text-sm font-normal text-gray-500">
                        ({{ cartStore.itemCount }} {{ cartStore.itemCount === 1 ? 'item' : 'itens' }})
                      </span>
                    </DialogTitle>
                    <button
                      type="button"
                      class="p-2 text-gray-400 hover:text-gray-500 rounded-lg hover:bg-gray-100"
                      @click="cartStore.closeDrawer"
                    >
                      <XMarkIcon class="w-6 h-6" />
                    </button>
                  </div>

                  <!-- Cart items -->
                  <div class="flex-1 overflow-y-auto px-4 py-6">
                    <!-- Empty state -->
                    <div
                      v-if="cartStore.isEmpty"
                      class="flex flex-col items-center justify-center h-full text-center"
                    >
                      <ShoppingBagIcon class="w-16 h-16 text-gray-300 mb-4" />
                      <h3 class="text-lg font-medium text-gray-900 mb-2">
                        Seu carrinho está vazio
                      </h3>
                      <p class="text-gray-500 mb-6">
                        Explore nossa loja e encontre soluções para sua empresa.
                      </p>
                      <RouterLink
                        to="/loja"
                        class="btn-primary"
                        @click="cartStore.closeDrawer"
                      >
                        Ver produtos
                      </RouterLink>
                    </div>

                    <!-- Items list -->
                    <ul v-else class="divide-y divide-gray-200">
                      <li
                        v-for="item in cartStore.items"
                        :key="item.id"
                        class="py-4 flex gap-4"
                      >
                        <!-- Thumbnail -->
                        <div class="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            v-if="item.thumbnail_url"
                            :src="item.thumbnail_url"
                            :alt="item.name"
                            class="w-full h-full object-cover"
                          />
                          <div
                            v-else
                            class="w-full h-full flex items-center justify-center text-gray-400"
                          >
                            <ShoppingBagIcon class="w-8 h-8" />
                          </div>
                        </div>

                        <!-- Info -->
                        <div class="flex-1 min-w-0">
                          <RouterLink
                            :to="`/loja/${item.slug}`"
                            class="text-sm font-medium text-gray-900 hover:text-primary-600 line-clamp-2"
                            @click="cartStore.closeDrawer"
                          >
                            {{ item.name }}
                          </RouterLink>
                          <p class="mt-1 text-sm text-gray-500">
                            {{ formatCurrency(item.price) }}
                          </p>

                          <!-- Quantity controls -->
                          <div class="mt-2 flex items-center gap-2">
                            <button
                              type="button"
                              class="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                              @click="cartStore.decrementQuantity(item.id)"
                            >
                              <MinusIcon class="w-4 h-4" />
                            </button>
                            <span class="text-sm font-medium text-gray-900 w-8 text-center">
                              {{ item.quantity }}
                            </span>
                            <button
                              type="button"
                              class="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                              @click="cartStore.incrementQuantity(item.id)"
                            >
                              <PlusIcon class="w-4 h-4" />
                            </button>

                            <button
                              type="button"
                              class="ml-auto p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                              @click="cartStore.removeItem(item.id)"
                            >
                              <TrashIcon class="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>

                  <!-- Footer with totals -->
                  <div v-if="!cartStore.isEmpty" class="border-t border-gray-200 px-4 py-6">
                    <!-- Coupon -->
                    <div v-if="cartStore.coupon" class="mb-4 p-3 bg-secondary-50 rounded-lg flex items-center justify-between">
                      <div>
                        <p class="text-sm font-medium text-secondary-800">
                          Cupom: {{ cartStore.coupon.code }}
                        </p>
                        <p class="text-xs text-secondary-600">
                          {{ cartStore.coupon.discount_type === 'percentage' 
                            ? `${cartStore.coupon.discount_value}% de desconto`
                            : `${formatCurrency(cartStore.coupon.discount_value)} de desconto`
                          }}
                        </p>
                      </div>
                      <button
                        type="button"
                        class="text-secondary-600 hover:text-secondary-800"
                        @click="cartStore.removeCoupon"
                      >
                        <XMarkIcon class="w-5 h-5" />
                      </button>
                    </div>

                    <!-- Totals -->
                    <div class="space-y-2 mb-6">
                      <div class="flex justify-between text-sm">
                        <span class="text-gray-500">Subtotal</span>
                        <span class="text-gray-900">{{ formatCurrency(cartStore.subtotal) }}</span>
                      </div>
                      <div v-if="cartStore.discount > 0" class="flex justify-between text-sm">
                        <span class="text-secondary-600">Desconto</span>
                        <span class="text-secondary-600">-{{ formatCurrency(cartStore.discount) }}</span>
                      </div>
                      <div class="flex justify-between text-lg font-semibold border-t border-gray-200 pt-2">
                        <span>Total</span>
                        <span>{{ formatCurrency(cartStore.total) }}</span>
                      </div>
                    </div>

                    <!-- Actions -->
                    <div class="space-y-3">
                      <RouterLink
                        to="/checkout"
                        class="btn-primary w-full justify-center"
                        @click="cartStore.closeDrawer"
                      >
                        Finalizar compra
                      </RouterLink>
                      <button
                        type="button"
                        class="btn-ghost w-full justify-center text-sm"
                        @click="cartStore.closeDrawer"
                      >
                        Continuar comprando
                      </button>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
